import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import {
  TextField,
  CircularProgress,
  Box,
  Typography,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { styled } from "@mui/material/styles";
import useUserStore from "../../../store/user";
import { ReportsService } from "../services";
import { toast } from "react-toastify";

interface Profile {
  profileBusinessId: string;
  razaoSocial: string;
  locatidade: string;
  email: string;
  updatedAt: string;
  profileStatus: "ATENCAO" | "CRITICO" | "OK" | "null";
  rankedKeywords: number;
  totalKeywords: number;
  tracking: boolean;
  isFavorite: boolean;
}

const PageContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #f8f9fa 100%);
`;

const HeaderContainer = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 32px 40px;
  background: #ffffff;
  border-bottom: 2px solid #f0f0f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
    padding: 24px;
  }
`;

const SearchContainer = styled(Box)`
  display: flex;
  gap: 12px;
  flex: 1;
  max-width: 600px;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
  }
`;

const ContentContainer = styled(Box)`
  flex: 1;
  padding: 32px 40px;
  overflow: auto;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const ActionContainer = styled(Box)`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px 32px;
  background: #ffffff;
  border-top: 1px solid #e0e0e0;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 16px;
  }
`;

const EmptyStateContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 20px;
  text-align: center;

  svg {
    font-size: 80px;
    color: #e8eaed;
    margin-bottom: 24px;
  }

  h3 {
    color: #202124;
    font-size: 20px;
    font-weight: 600;
    margin: 0 0 12px 0;
  }

  p {
    color: #80868b;
    font-size: 15px;
    margin: 0;
  }
`;

const LoadingContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 100px 20px;
  gap: 16px;

  span {
    color: #5f6368;
    font-size: 15px;
  }
`;

const StyledTableContainer = styled(TableContainer)`
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #f0f0f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  overflow: hidden;

  @media (max-width: 768px) {
    border-radius: 8px;
  }
`;

const StyledTableRow = styled(TableRow)<{ $isSelected?: boolean }>`
  background-color: ${(props) => (props.$isSelected ? "#e3f2fd" : "#ffffff")};
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid #f0f0f0;

  &:hover {
    background-color: ${(props) => (props.$isSelected ? "#bbdefb" : "#f8f9fa")};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const StyledTableCell = styled(TableCell)`
  padding: 18px 16px;
  color: #202124;
  font-size: 14px;
  font-weight: 500;
  vertical-align: middle;

  @media (max-width: 768px) {
    padding: 14px 12px;
    font-size: 13px;
  }
`;

const HeaderTableCell = styled(TableCell)`
  padding: 18px 16px;
  background-color: #f8f9fa;
  color: #5f6368;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e8eaed;

  @media (max-width: 768px) {
    padding: 14px 12px;
    font-size: 12px;
  }
`;

const CheckboxCell = styled(TableCell)`
  width: 50px;
  padding: 18px 12px;

  input {
    cursor: pointer;
    width: 18px;
    height: 18px;
  }
`;


export const ReportsProfileSelection: React.FC<{ reportType?: string }> = ({
  reportType = "keywords",
}) => {
  const { user } = useUserStore();
  const navigate = useNavigate();
  // Cliente: /customers/tracking é só da equipe ("Usuário sem permissão", visto em 01/10/2026).
  // Ele já tem o perfil escolhido no login; vai direto ao relatório dele.
  const perfilDoCliente =
    user?.userType === "CUSTOMER"
      ? (user.businessProfiles?.[0] ?? "").match(/[0-9a-f-]{36}/i)?.[0] ?? null
      : null;
  useEffect(() => {
    if (perfilDoCliente) navigate(`/reports/${reportType}/${perfilDoCliente}`, { replace: true });
  }, [perfilDoCliente, reportType, navigate]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedProfile, setSelectedProfile] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 20,
    totalCount: 0,
  });
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handlePageChange = useCallback(
    (_event: unknown, newPage: number) => {
      setPagination((p) => ({ ...p, page: newPage }));
    },
    []
  );

  /**
   * Altera o tamanho da paginação e reseta para a primeira página.
   * 
   * @param event - Evento de mudança do input
   */
  const handlePageSizeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPagination((p) => ({
        ...p,
        pageSize: parseInt(event.target.value, 10),
        page: 0,
      }));
    },
    []
  );

  /**
   * Executa busca com delay para evitar requisições excessivas.
   * Ao alterar o termo de busca, reseta a paginação para a primeira página.
   */
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      if (pagination.page !== 0) {
        setPagination((p) => ({ ...p, page: 0 }));
      }
    }, 200);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchTerm]);

  /**
   * Carrega perfis quando há mudanças na paginação ou termo de busca.
   * Trata erros de autenticação e falhas na requisição.
   */
  useEffect(() => {
    const fetchData = async () => {
      if (perfilDoCliente) return;
      if (!user?.token) {
        toast.warn("Sessão expirada. Faça login novamente.");
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await ReportsService.getMyProfiles({
          token: user.token,
          page: pagination.page,
          size: pagination.pageSize,
          query: debouncedSearchTerm || undefined,
        });

        if (response?.content && Array.isArray(response.content)) {
          setProfiles(response.content);
          setPagination((prev) => ({
            ...prev,
            totalCount: response.totalElements,
          }));
        } else {
          setProfiles([]);
          setPagination((prev) => ({ ...prev, totalCount: 0 }));
        }
      } catch (error) {
        console.error("Erro ao buscar perfis:", error);
        toast.error("Falha ao carregar perfis. Tente novamente.");
        setProfiles([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.token, pagination.page, pagination.pageSize, debouncedSearchTerm, perfilDoCliente]);

  /**
   * Atualiza o termo de busca para filtrar perfis.
   * 
   * @param value - Novo termo de busca
   */
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  /**
   * Alterna a seleção de um perfil (ativa/desativa).
   * Apenas um perfil pode estar selecionado por vez.
   * 
   * @param profileId - Identificador único do perfil a selecionar
   */
  const handleSelectProfile = (profileId: string) => {
    if (selectedProfile === profileId) {
      setSelectedProfile(null);
    } else {
      setSelectedProfile(profileId);
    }
  };

  /**
   * Navega para a página de relatório do perfil selecionado.
   */
  const handleContinue = () => {
    if (selectedProfile) {
      navigate(`/reports/${reportType}/${selectedProfile}`);
    }
  };

  /**
   * Volta para a página inicial.
   */
  const handleBack = () => {
    navigate("/home");
  };

  const reportTitle =
    reportType === "keywords"
      ? "Relatórios de Palavras-Chave"
      : "Relatório de Performance";

  if (loading && profiles.length === 0) {
    return (
      <PageContainer>
        <HeaderContainer>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {reportTitle}
          </Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            variant="outlined"
          >
            Voltar
          </Button>
        </HeaderContainer>
        <ContentContainer>
          <LoadingContainer>
            <CircularProgress />
          </LoadingContainer>
        </ContentContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <HeaderContainer>
        <Box>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700, 
              mb: 1,
              color: "#202124",
              fontSize: "24px"
            }}
          >
            {reportTitle}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: "#80868b",
              fontSize: "15px",
              fontWeight: 400
            }}
          >
            Selecione um perfil para visualizar o relatório detalhado
          </Typography>
        </Box>
        <Button
          startIcon={<ArrowBackIcon sx={{ fontSize: "18px" }} />}
          onClick={handleBack}
          variant="outlined"
          sx={{
            textTransform: "none",
            fontSize: "15px",
            fontWeight: 500,
            borderRadius: "8px",
            padding: "10px 20px",
            borderColor: "#dadce0",
            color: "#202124",
            "&:hover": {
              borderColor: "#1976d2",
              backgroundColor: "#f8f9fa",
            }
          }}
        >
          Voltar
        </Button>
      </HeaderContainer>

      <ContentContainer>
        <Box sx={{ mb: 3 }}>
          <SearchContainer>
            <TextField
              placeholder="Buscar perfil por nome..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1.5, color: "#9aa0a6", fontSize: "20px" }} />,
              }}
              fullWidth
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  backgroundColor: "#ffffff",
                  border: "2px solid #e8eaed",
                  transition: "all 0.2s ease",
                  fontSize: "15px",
                  "& fieldset": {
                    border: "none",
                  },
                  "&:hover": {
                    borderColor: "#dadce0",
                    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                  },
                  "&.Mui-focused": {
                    borderColor: "#1976d2",
                    boxShadow: "0 1px 4px rgba(25, 118, 210, 0.15)",
                  },
                },
                "& .MuiOutlinedInput-input": {
                  padding: "12px 16px",
                  color: "#202124",
                  "&::placeholder": {
                    color: "#9aa0a6",
                    opacity: 1,
                  },
                },
              }}
            />
          </SearchContainer>
        </Box>

        {profiles.length === 0 ? (
          <EmptyStateContainer>
            <SearchIcon />
            <h3>Nenhum perfil encontrado</h3>
            <p>
              {searchTerm
                ? "Tente buscar com outro termo"
                : "Você não possui perfis de negócio configurados"}
            </p>
          </EmptyStateContainer>
        ) : (
          <StyledTableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <CheckboxCell padding="checkbox">
                    Selecionar
                  </CheckboxCell>
                  <HeaderTableCell>Nome</HeaderTableCell>
                  <HeaderTableCell>Localidade</HeaderTableCell>
                  <HeaderTableCell>Email</HeaderTableCell>
                  <HeaderTableCell>Palavras-chave Ranqueadas</HeaderTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {profiles.map((profile) => (
                  <StyledTableRow
                    key={profile.profileBusinessId}
                    $isSelected={selectedProfile === profile.profileBusinessId}
                    onClick={() =>
                      handleSelectProfile(profile.profileBusinessId)
                    }
                  >
                    <CheckboxCell padding="checkbox">
                      <input
                        type="radio"
                        name="profile"
                        checked={selectedProfile === profile.profileBusinessId}
                        onChange={() =>
                          handleSelectProfile(profile.profileBusinessId)
                        }
                        onClick={(e) => e.stopPropagation()}
                      />
                    </CheckboxCell>
                    <StyledTableCell sx={{ fontWeight: 600, color: "#1976d2" }}>
                      {profile.razaoSocial || "Perfil Sem Nome"}
                    </StyledTableCell>
                    <StyledTableCell>{profile.locatidade || "-"}</StyledTableCell>
                    <StyledTableCell sx={{ color: "#5f6368", fontSize: "13px" }}>{profile.email || "-"}</StyledTableCell>
                    <StyledTableCell sx={{ textAlign: "center", fontWeight: 600, color: "#1976d2" }}>
                      {profile.rankedKeywords || 0}
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[20, 50, 100]}
              component="div"
              count={pagination.totalCount}
              rowsPerPage={pagination.pageSize}
              page={pagination.page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handlePageSizeChange}
              labelRowsPerPage="Perfis por página:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from} a ${to} de ${count} perfis`
              }
              sx={{
                borderTop: "1px solid #f0f0f0",
                backgroundColor: "#ffffff",
                "& .MuiTablePagination-root": {
                  fontSize: "14px",
                },
              }}
            />
          </StyledTableContainer>
        )}
      </ContentContainer>

      <ActionContainer>
        <Button 
          variant="outlined" 
          onClick={handleBack}
          sx={{
            textTransform: "none",
            fontSize: "15px",
            fontWeight: 500,
            borderRadius: "8px",
            padding: "10px 24px",
            "&:hover": {
              background: "#f8f9fa",
            }
          }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleContinue}
          disabled={!selectedProfile}
          sx={{
            textTransform: "none",
            fontSize: "15px",
            fontWeight: 600,
            borderRadius: "8px",
            padding: "10px 32px",
            background: selectedProfile ? "#1976d2" : "#ccc",
            "&:hover": {
              background: selectedProfile ? "#1565c0" : "#ccc",
            }
          }}
        >
          Continuar para Relatórios
        </Button>
      </ActionContainer>
    </PageContainer>
  );
};
